import { type JSX, useEffect, useMemo, useState } from 'react';

import type { Terminauswahl } from '../api/termine';
import { Dialog } from '../komponenten/Dialog';

/**
 * Den Termin einer neuen Buchung im Kalender wählen (BUC-80; 19.09.2026) —
 * und seit CRM-94 den Zieltermin einer Umbuchung (BUC-51).
 *
 * „Bei Termin soll sich ein Kalender öffnen."
 *
 * DIE TERMINE KOMMEN VON DER MASKE — die buchbaren des gewählten Produkts,
 * mit der freien Kapazität für DIESEN Kunden (KAP-04). Der Kalender zeigt
 * sie nur anders an: Ein Monatsraster beantwortet „wann geht es?" schneller
 * als eine Liste mit Uhrzeiten.
 *
 * FRS-15: Ein Termin mit zu wenig Platz steht da, ist aber nicht wählbar —
 * und sagt, warum. Ein weggelassener Termin hieße für den Anrufer „es gibt
 * keinen", und das stimmt nicht.
 *
 * DIE ZONE STEHT FEST (FRS-01): Tag und Uhrzeit in Europe/Berlin, wie im
 * Terminkalender.
 *
 * ============================================================================
 * KEIN KNOPF ZUR WARTELISTE (BUC-11 nach ÄA-55)
 * ============================================================================
 *
 * Bis ÄA-55 trug ein voller Termin hier „Auf die Warteliste". Gewartet wird
 * seither auf ein Training und mit einer E-Mail-Adresse, nicht auf diesen
 * Termin und mit diesem Kunden; eingetragen wird in der Artikelakte.
 */

const tagschluessel = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' });
const monatform = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const tagform = new Intl.DateTimeFormat('de-DE', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
});
const zeitform = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Europe/Berlin',
});

/** Der Kalendertag eines Zeitpunkts in Ortszeit, als `JJJJ-MM-TT`. */
function tagVon(iso: string): string {
  return tagschluessel.format(new Date(iso));
}

/** Das Raster beginnt montags (DIN 1355-1) und hat sechs Wochen. */
function raster(jahr: number, monat: number): readonly Date[] {
  const erster = new Date(Date.UTC(jahr, monat, 1));
  erster.setUTCDate(erster.getUTCDate() - ((erster.getUTCDay() + 6) % 7));
  return Array.from({ length: 42 }, (_, i) => new Date(erster.getTime() + i * 86_400_000));
}

export function Terminkalenderwahl({
  termine,
  plaetze,
  gewaehlt,
  titel = 'Termin auswählen',
  beiWahl,
  beiSchliessen,
}: {
  readonly termine: readonly Terminauswahl[];
  /** Die Plätze der Buchung — was weniger frei hat, ist nicht wählbar. */
  readonly plaetze: number;
  /** Die Überschrift des Fensters; die Umbuchung fragt nach dem Zieltermin. */
  readonly titel?: string;
  readonly gewaehlt: string;
  readonly beiWahl: (termin: Terminauswahl) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const jeTag = useMemo(() => {
    const karte = new Map<string, Terminauswahl[]>();
    for (const t of [...termine].sort((a, b) => a.beginn.localeCompare(b.beginn))) {
      const tag = tagVon(t.beginn);
      karte.set(tag, [...(karte.get(tag) ?? []), t]);
    }
    return karte;
  }, [termine]);

  /*
   * DER KALENDER GEHT DORT AUF, WO ES ETWAS GIBT — beim gewählten Termin oder
   * beim ersten buchbaren. Ein Kalender, der im laufenden Monat aufgeht,
   * während der erste Termin im März liegt, zeigt zuerst sechs leere Wochen.
   */
  const start =
    termine.find((t) => t.id === gewaehlt)?.beginn
    ?? [...termine].sort((a, b) => a.beginn.localeCompare(b.beginn))[0]?.beginn;
  const startTag = start === undefined ? tagschluessel.format(new Date()) : tagVon(start);

  const [jahr, setzeJahr] = useState(Number(startTag.slice(0, 4)));
  const [monat, setzeMonat] = useState(Number(startTag.slice(5, 7)) - 1);
  const [tag, setzeTag] = useState<string | null>(start === undefined ? null : startTag);

  /*
   * KOMMEN DIE TERMINE ERST NACH DEM ÖFFNEN, springt der Kalender nach —
   * solange noch kein Tag gewählt ist. Sonst bliebe er im laufenden Monat
   * stehen, und der Anwender blätterte zu Terminen, die das Fenster kennt.
   */
  useEffect(() => {
    if (tag !== null || start === undefined) return;
    setzeJahr(Number(startTag.slice(0, 4)));
    setzeMonat(Number(startTag.slice(5, 7)) - 1);
    setzeTag(startTag);
  }, [tag, start, startTag]);

  const blaettere = (schritt: number): void => {
    const d = new Date(Date.UTC(jahr, monat + schritt, 1));
    setzeJahr(d.getUTCFullYear());
    setzeMonat(d.getUTCMonth());
  };

  const tage = raster(jahr, monat);
  const desTages = tag === null ? [] : (jeTag.get(tag) ?? []);

  return (
    <Dialog titel={titel} breite="breit" beiSchliessen={beiSchliessen}>
      <div className="knopfreihe">
        <button type="button" className="knopf knopf--klein" onClick={() => blaettere(-1)}>
          ← Vormonat
        </button>
        <strong aria-live="polite">{monatform.format(new Date(Date.UTC(jahr, monat, 1)))}</strong>
        <button type="button" className="knopf knopf--klein" onClick={() => blaettere(1)}>
          Folgemonat →
        </button>
        <span className="feld__hilfe">
          {termine.length === 1 ? '1 buchbarer Termin' : `${String(termine.length)} buchbare Termine`}
        </span>
      </div>

      <div className="tabellenhuelle tabellenhuelle--telefon">
        <table className="liste__tabelle kalender">
          <caption className="nur-vorlesen">Monatsübersicht der buchbaren Termine</caption>
          <thead>
            <tr>
              {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map((w) => (
                <th key={w} scope="col">
                  {w}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2, 3, 4, 5].map((woche) => (
              <tr key={woche}>
                {tage.slice(woche * 7, woche * 7 + 7).map((d) => {
                  const schluessel = d.toISOString().slice(0, 10);
                  const liste = jeTag.get(schluessel) ?? [];
                  const frei = liste.reduce((n, t) => n + t.frei, 0);
                  const klasse = [
                    d.getUTCMonth() !== monat ? 'kalender__fremd' : '',
                    schluessel === tag ? 'kalender__gewaehlt' : '',
                  ]
                    .filter((k) => k !== '')
                    .join(' ');
                  return (
                    <td key={schluessel} className={klasse === '' ? undefined : klasse}>
                      {liste.length === 0 ? (
                        <span className="kalender__tag">{d.getUTCDate()}</span>
                      ) : (
                        <button
                          type="button"
                          className="kalender__knopf"
                          aria-pressed={schluessel === tag}
                          aria-label={`${tagform.format(d)}: ${String(liste.length)} Termin${liste.length === 1 ? '' : 'e'}, ${String(frei)} Plätze frei`}
                          onClick={() => setzeTag(schluessel)}
                        >
                          <span className="kalender__tag">{d.getUTCDate()}</span>
                          <span className="kalender__zahl">
                            {liste.length} · {frei} frei
                          </span>
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {tag !== null && desTages.length > 0 && (
        <section aria-label="Termine des Tages">
          <h3>{tagform.format(new Date(`${tag}T00:00:00Z`))}</h3>
          <ul className="terminwahl__liste">
            {desTages.map((t) => {
              const zuWenig = t.frei < plaetze;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    className={`knopf${t.id === gewaehlt ? ' knopf--haupt' : ''}`}
                    disabled={zuWenig}
                    onClick={() => beiWahl(t)}
                  >
                    {zeitform.format(new Date(t.beginn))}–{zeitform.format(new Date(t.ende))} Uhr ·{' '}
                    {t.frei} von {t.hoechstteilnehmer} frei
                  </button>
                  {zuWenig && (
                    <span className="feld__hilfe">
                      {' '}
                      zu wenig frei für {plaetze} {plaetze === 1 ? 'Platz' : 'Plätze'}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {termine.length === 0 && (
        <p className="liste__leer">Für dieses Produkt gibt es keinen buchbaren Termin.</p>
      )}
    </Dialog>
  );
}
